// Copied from kettle-platform workers/admin/ui/src/charts/Stacked.tsx (2026-10-30); restyled for Estra, see docs/kettle-sharing.md.
// Daily stacked chart: bars (counts that split a whole, e.g. reads by collection) or
// smooth stacked areas (a total built from parts over time, e.g. cost by resource). Colours are
// fixed per layer, a hover/focus tooltip lists each layer and the total, and a table view
// gives the same numbers.
import { useId, useMemo, useState, type PointerEvent } from "react";
import { niceMax, runs, smooth } from "./scale";

export interface Layer {
  id: string;
  label: string;
  color: string;
  values: number[];
}

const W = 640;
const H = 200;
const PAD = { l: 48, r: 8, t: 12, b: 24 };

export function Stacked({ title, days, layers, format, mode, note }: { title: string; days: string[]; layers: Layer[]; format: (n: number) => string; mode: "bars" | "area"; note?: string }) {
  const id = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const n = days.length;
  const totals = days.map((_, i) => layers.reduce((s, l) => s + (l.values[i] ?? 0), 0));
  const max = useMemo(() => niceMax(Math.max(0, ...totals)), [totals]);
  const y = (v: number) => PAD.t + (H - PAD.t - PAD.b) * (1 - v / max);
  const slot = (W - PAD.l - PAD.r) / Math.max(n, 1);
  const cx = (i: number) => (mode === "bars" ? PAD.l + i * slot + slot / 2 : PAD.l + ((W - PAD.l - PAD.r) * i) / Math.max(n - 1, 1));
  const bw = Math.max(2, Math.min(18, slot - 2));
  // Running tops per layer: layer k sits on layers 0..k-1.
  const tops = layers.map((_, k) => days.map((_, i) => layers.slice(0, k + 1).reduce((s, l) => s + (l.values[i] ?? 0), 0)));
  const shown = hover ?? n - 1;
  const total = totals.reduce((s, v) => s + v, 0);

  const pick = (e: PointerEvent<SVGRectElement>) => {
    const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = mode === "bars" ? Math.floor((px - PAD.l) / slot) : Math.round(((px - PAD.l) / (W - PAD.l - PAD.r)) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };

  return (
    <figure className="chart" aria-labelledby={`${id}-t`}>
      <figcaption>
        <div>
          <span id={`${id}-t`} className="chart-title">{title}</span>
          <span className="chart-total">
            {hover === null ? (
              <><strong>{format(total)}</strong> in {n} days{note ? ` · ${note}` : ""}</>
            ) : (
              <><strong>{format(totals[hover]!)}</strong> on {days[hover]}</>
            )}
          </span>
        </div>
        <button className="linkish" onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>{asTable ? "Chart" : "Table"}</button>
      </figcaption>
      {asTable ? (
        <div className="table-wrap small">
          <table>
            <thead><tr><th>Day</th>{layers.map((l) => <th key={l.id} className="num">{l.label}</th>)}<th className="num">Total</th></tr></thead>
            <tbody>
              {[...days.keys()].reverse().map((i) => (
                <tr key={days[i]}><td>{days[i]}</td>{layers.map((l) => <td key={l.id} className="num">{format(l.values[i] ?? 0)}</td>)}<td className="num">{format(totals[i]!)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${title}: ${layers.map((l) => l.label).join(", ")} per day for ${n} days. Use the table view for exact numbers.`} onPointerLeave={() => setHover(null)}>
          {[0, max / 2, max].map((t) => (
            <g key={t}>
              <line className={t === 0 ? "baseline" : "grid"} x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} />
              <text className="axis" x={PAD.l - 6} y={y(t) + 4} textAnchor="end">{format(t)}</text>
            </g>
          ))}
          {mode === "area"
            ? layers.map((l, k) => {
                const top = runs(tops[k]!, cx, y)[0] ?? [];
                const bottom = k === 0 ? days.map((_, i) => [cx(i), y(0)] as [number, number]) : (runs(tops[k - 1]!, cx, y)[0] ?? []);
                if (top.length < 2) return null;
                const back = smooth([...bottom].reverse()).replace(/^M/, "L");
                return <path key={l.id} className="stack-area" d={`${smooth(top)}${back}Z`} fill={l.color} stroke={l.color} />;
              })
            : days.map((_, i) =>
                layers.map((l, k) => {
                  const lo = k === 0 ? 0 : tops[k - 1]![i]!;
                  const hi = tops[k]![i]!;
                  if (hi <= lo) return null;
                  return <rect key={`${l.id}${i}`} className={`stack-bar${hover !== null && hover !== i ? " dim" : ""}`} x={cx(i) - bw / 2} y={y(hi)} width={bw} height={y(lo) - y(hi)} fill={l.color} rx={k === layers.length - 1 ? Math.min(3, bw / 2) : 0} />;
                }),
              )}
          {hover !== null && mode === "area" && <line className="cross" x1={cx(hover)} x2={cx(hover)} y1={PAD.t} y2={H - PAD.b} />}
          {n > 0 && (
            <>
              <text className="axis" x={PAD.l} y={H - 6}>{days[0]!.slice(5)}</text>
              <text className="axis" x={W - PAD.r} y={H - 6} textAnchor="end">{days[n - 1]!.slice(5)}</text>
            </>
          )}
          <rect x={PAD.l} y={0} width={W - PAD.l - PAD.r} height={H - PAD.b} fill="transparent" onPointerMove={pick} onPointerDown={pick} />
        </svg>
      )}
      <div className="stack-legend">
        {layers.map((l) => (
          <span key={l.id}><i style={{ background: l.color }} />{l.label} <b>{format(l.values[shown] ?? 0)}</b></span>
        ))}
        <span className="muted small">{hover === null ? "latest day" : days[hover]}</span>
      </div>
    </figure>
  );
}
